export const TOLERANCIA_MONETARIA = 0.01;
export const VENTA_PENDIENTE_STORAGE = "PosVentaPendienteV2";

const numero = (valor) => {
  const resultado = Number(valor);
  return Number.isFinite(resultado) ? resultado : 0;
};

export const redondearMoneda = (valor) => {
  const n = numero(valor);
  return Math.round((n + Math.sign(n || 1) * Number.EPSILON) * 100) / 100;
};

const tomarCampo = (objeto, nombres) => {
  for (const nombre of nombres) {
    if (objeto?.[nombre] !== undefined && objeto?.[nombre] !== null && objeto?.[nombre] !== "") {
      return objeto[nombre];
    }
  }
  return undefined;
};

export const obtenerIvaMercaderia = (mercaderia) => {
  const porcentajeCrudo = tomarCampo(mercaderia, [
    "PorcentajeIVA",
    "PorcentajeIva",
    "nPorcentajeIVA",
    "nPorcentajeIva",
    "IvaPorcentaje",
    "IVAPorcentaje",
    "AlicuotaIVA",
    "nIVA",
  ]);
  const porcentajeReal = porcentajeCrudo ?? mercaderia?.IVA?.Porcentaje ?? mercaderia?.Iva?.Porcentaje;
  const condicionCruda = tomarCampo(mercaderia, ["ConSinIVA", "ConSinIva", "bConSinIVA", "bIVA", "LlevaIVA"]);

  if (porcentajeReal === undefined && condicionCruda === undefined) {
    return {
      valido: true,
      conocido: false,
      ConSinIVA: false,
      PorcentajeIVA: 0,
      mensaje: "IVA calculado al confirmar",
    };
  }

  const porcentaje = numero(porcentajeReal);
  const conSinIVA = condicionCruda === undefined
    ? porcentaje > 0
    : typeof condicionCruda === "string"
      ? ["true", "1", "si", "sí"].includes(condicionCruda.toLowerCase())
      : Boolean(condicionCruda);
  if (conSinIVA && porcentajeReal === undefined) {
    return { valido: false, error: "La mercaderia aplica IVA pero no informa su porcentaje." };
  }
  if (porcentaje < 0 || porcentaje > 100) {
    return { valido: false, error: "La mercaderia informa un porcentaje de IVA invalido." };
  }
  return { valido: true, conocido: true, ConSinIVA: conSinIVA, PorcentajeIVA: porcentaje };
};

export const calcularVentaLinea = (linea = {}) => {
  const cantidad = numero(linea.Cantidad);
  const precioUnitario = numero(linea.PrecioUnitario);
  const descuentoLinea = numero(linea.DescuentoLinea);
  const porcentajeIVA = numero(linea.PorcentajeIVA);
  const importeBruto = redondearMoneda(cantidad * precioUnitario);
  const subtotal = redondearMoneda(importeBruto - descuentoLinea);
  const ivaLinea = linea.ConSinIVA
    ? redondearMoneda((subtotal * porcentajeIVA) / 100)
    : 0;
  return { importeBruto, subtotal, ivaLinea };
};

export const calcularVentaResumen = (lineas = []) => {
  const acumulado = lineas.reduce(
    (resultado, linea) => {
      const calculo = calcularVentaLinea(linea);
      resultado.SubtotalBruto += calculo.importeBruto;
      resultado.DescuentoTotal += numero(linea.DescuentoLinea);
      if (linea.ConSinIVA && linea.IVAConocido !== false) {
        const tasa = numero(linea.PorcentajeIVA);
        resultado.BasesIva.set(tasa, (resultado.BasesIva.get(tasa) || 0) + calculo.subtotal);
      }
      return resultado;
    },
    { SubtotalBruto: 0, DescuentoTotal: 0, BasesIva: new Map() }
  );
  const SubtotalBruto = redondearMoneda(acumulado.SubtotalBruto);
  const DescuentoTotal = redondearMoneda(acumulado.DescuentoTotal);
  const Total = redondearMoneda(SubtotalBruto - DescuentoTotal);
  const AlicuotasIVA = [...acumulado.BasesIva.entries()]
    .sort(([a], [b]) => a - b)
    .map(([Porcentaje, BaseImponible]) => ({
      Porcentaje,
      BaseImponible: redondearMoneda(BaseImponible),
      ImporteIVA: redondearMoneda((BaseImponible * Porcentaje) / 100),
    }));
  const IvaTotal = redondearMoneda(AlicuotasIVA.reduce((total, alicuota) => total + alicuota.ImporteIVA, 0));
  return {
    SubtotalBruto,
    DescuentoTotal,
    Total,
    IvaTotal,
    TotalConIva: redondearMoneda(Total + IvaTotal),
    AlicuotasIVA,
    EsEstimado: lineas.some((linea) => !linea.IvaPendiente && linea.IVAConocido === false),
  };
};

const coincide = (informado, calculado) =>
  Math.abs(numero(informado) - numero(calculado)) <= TOLERANCIA_MONETARIA;

export const validarVenta = (lineas = [], resumenInformado) => {
  const erroresLineas = {};
  lineas.forEach((linea, indice) => {
    const errores = [];
    const cantidad = Number(linea.Cantidad);
    const precio = Number(linea.PrecioUnitario);
    const descuento = Number(linea.DescuentoLinea);
    const porcentaje = Number(linea.PorcentajeIVA);
    const calculo = calcularVentaLinea(linea);

    if (!Number.isFinite(cantidad) || cantidad <= 0) errores.push("La cantidad debe ser mayor que cero.");
    if (!Number.isFinite(precio) || precio < 0) errores.push("El precio unitario no puede ser negativo.");
    if (!Number.isFinite(descuento) || descuento < 0) errores.push("El descuento no puede ser negativo.");
    if (Number.isFinite(descuento) && descuento > calculo.importeBruto) {
      errores.push("El descuento no puede superar el importe bruto.");
    }
    if (linea.IVAConocido !== false && (!Number.isFinite(porcentaje) || porcentaje < 0 || porcentaje > 100)) {
      errores.push("El porcentaje de IVA debe estar entre 0 y 100.");
    }
    if (!coincide(linea.Subtotal, calculo.subtotal)) errores.push("El subtotal de la linea no es consistente.");
    if (!linea.ProductoNuevo && linea.PrecioVigente === false) {
      errores.push(linea.ErrorPrecio || "El precio no esta vigente para el cliente seleccionado.");
    }
    if (errores.length) erroresLineas[linea.Id ?? indice] = errores;
  });

  const resumen = calcularVentaResumen(lineas);
  const erroresResumen = [];
  if (resumenInformado) {
    for (const campo of ["SubtotalBruto", "DescuentoTotal", "Total", "IvaTotal", "TotalConIva"]) {
      if (!coincide(resumenInformado[campo], resumen[campo])) {
        erroresResumen.push(`${campo} no coincide con el detalle.`);
      }
    }
  }
  return { valido: Object.keys(erroresLineas).length === 0 && erroresResumen.length === 0, erroresLineas, erroresResumen, resumen };
};

export const prepararLineasVenta = (lineas = []) =>
  lineas.map((linea) => {
    const calculo = calcularVentaLinea(linea);
    return {
      ...linea,
      Cantidad: numero(linea.Cantidad),
      PrecioUnitario: redondearMoneda(linea.PrecioUnitario),
      DescuentoLinea: redondearMoneda(linea.DescuentoLinea),
      Subtotal: calculo.subtotal,
      ConSinIVA: Boolean(linea.ConSinIVA),
      PorcentajeIVA: numero(linea.PorcentajeIVA),
      IVAConocido: linea.IVAConocido !== false,
    };
  });

const ordenarObjeto = (valor) => {
  if (Array.isArray(valor)) return valor.map(ordenarObjeto);
  if (!valor || typeof valor !== "object") return valor;
  return Object.keys(valor).sort().reduce((resultado, clave) => {
    resultado[clave] = ordenarObjeto(valor[clave]);
    return resultado;
  }, {});
};

export const crearHuellaComercial = (contenido) => JSON.stringify(ordenarObjeto(contenido));

export const obtenerVentaPendiente = (storage = globalThis.localStorage) => {
  try {
    const valor = storage?.getItem(VENTA_PENDIENTE_STORAGE);
    return valor ? JSON.parse(valor) : null;
  } catch {
    return null;
  }
};

export const resolverIdempotencia = ({ contenidoComercial, request = contenidoComercial, cajaId, storage = globalThis.localStorage, uuidFactory }) => {
  const huella = crearHuellaComercial(contenidoComercial);
  const existente = obtenerVentaPendiente(storage);
  if (existente?.huella === huella && existente?.clave && existente?.request) return existente;
  const uuid = uuidFactory();
  const pendiente = {
    clave: `${String(cajaId || "caja")}-${uuid}`,
    huella,
    request,
    resultado: null,
    actualizadoEn: new Date().toISOString(),
  };
  storage?.setItem(VENTA_PENDIENTE_STORAGE, JSON.stringify(pendiente));
  return pendiente;
};

export const guardarResultadoPendiente = (pendiente, resultado, storage = globalThis.localStorage) => {
  const actualizado = { ...pendiente, resultado, actualizadoEn: new Date().toISOString() };
  storage?.setItem(VENTA_PENDIENTE_STORAGE, JSON.stringify(actualizado));
  return actualizado;
};

export const limpiarVentaPendiente = (storage = globalThis.localStorage) =>
  storage?.removeItem(VENTA_PENDIENTE_STORAGE);
